import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import { DefinitionList } from '../../components/ui';
import { sbg } from '../../theme/sbgTokens';
import { formatDateRange } from '../../utils/format';
import { TimeOffCategory, TimeOffStatus, TimeOffType } from '../../types/timeOff';
import type { TimeOffEntry } from '../../types/timeOff';
import { CATEGORY_LABELS, typeLabel } from './meta';
import { formatCalendarEventTime } from './text';

const toInputDate = (value?: string) => (value ? format(parseISO(value), 'yyyy-MM-dd') : '');

export interface EditEntryDialogProps {
  entry: TimeOffEntry | null;
  /** The entry as it was when the dialog opened, for the subtitle. */
  original: TimeOffEntry | null;
  employeeName: string;
  saving: boolean;
  error: string | null;
  onChange: (entry: TimeOffEntry) => void;
  onClose: () => void;
  onSave: () => void;
}

const EditEntryDialog: React.FC<EditEntryDialogProps> = ({
  entry,
  original,
  employeeName,
  saving,
  error,
  onChange,
  onClose,
  onSave,
}) => {
  const titleId = React.useId();
  const descriptionId = React.useId();
  // Graph stores event times in the event's own zone; show them once, in ET.
  const eventTime = entry ? formatCalendarEventTime(entry) : null;
  const calendarDetails = entry
    ? [
        entry.originalCalendarSubject ? { label: 'Subject', value: entry.originalCalendarSubject } : null,
        eventTime ? { label: 'Time', value: eventTime } : null,
        entry.originalCalendarLocation ? { label: 'Location', value: entry.originalCalendarLocation } : null,
      ].filter((item): item is { label: string; value: string } => item !== null)
    : [];

  return (
    <Dialog
      open={!!entry}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
    >
      <DialogTitle id={titleId}>Edit time-off entry</DialogTitle>
      <DialogContent>
        {entry && (
          <Box sx={{ display: 'grid', gap: 2.5 }}>
            <Typography id={descriptionId} variant="body1" component="p" sx={{ color: sbg.text.secondary }}>
              {original
                ? `${employeeName} · ${typeLabel(original.type)} · ${formatDateRange(original.startDate, original.endDate)}`
                : employeeName}
            </Typography>

            {error && <Alert severity="error">{error}</Alert>}

            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' } }}>
              <TextField
                label="Start date"
                type="date"
                fullWidth
                value={toInputDate(entry.startDate)}
                onChange={(event) => onChange({ ...entry, startDate: event.target.value })}
              />
              <TextField
                label="End date"
                type="date"
                fullWidth
                value={toInputDate(entry.endDate)}
                onChange={(event) => onChange({ ...entry, endDate: event.target.value })}
              />
              <TextField
                select
                label="Type"
                fullWidth
                value={entry.type}
                onChange={(event) => onChange({ ...entry, type: event.target.value as TimeOffType })}
              >
                <MenuItem value={TimeOffType.PTO}>PTO</MenuItem>
                <MenuItem value={TimeOffType.SICK}>Sick time</MenuItem>
                <MenuItem value={TimeOffType.WFH}>Work from home</MenuItem>
              </TextField>
              <TextField
                select
                label="Category"
                fullWidth
                value={entry.category || ''}
                onChange={(event) => onChange({ ...entry, category: event.target.value as TimeOffCategory })}
                slotProps={{ select: { displayEmpty: true } }}
              >
                <MenuItem value="" disabled>
                  Choose a category
                </MenuItem>
                {Object.values(TimeOffCategory).map((category) => (
                  <MenuItem key={category} value={category}>
                    {CATEGORY_LABELS[category] ?? category}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Hours"
                type="number"
                fullWidth
                value={entry.hours}
                onChange={(event) => onChange({ ...entry, hours: Number(event.target.value) })}
                slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              />
              <TextField
                select
                label="Status"
                fullWidth
                value={entry.status}
                onChange={(event) => onChange({ ...entry, status: event.target.value as TimeOffStatus })}
              >
                <MenuItem value={TimeOffStatus.PENDING}>Pending</MenuItem>
                <MenuItem value={TimeOffStatus.APPROVED}>Approved</MenuItem>
                <MenuItem value={TimeOffStatus.REJECTED}>Rejected</MenuItem>
              </TextField>
            </Box>

            <TextField
              label="Reason"
              fullWidth
              value={entry.reason || ''}
              onChange={(event) => onChange({ ...entry, reason: event.target.value })}
            />
            <TextField
              label="Notes"
              multiline
              minRows={3}
              maxRows={8}
              fullWidth
              value={entry.notes || ''}
              onChange={(event) => onChange({ ...entry, notes: event.target.value })}
            />

            {calendarDetails.length > 0 && (
              <Box sx={{ bgcolor: sbg.bg.surfaceSubtle, borderRadius: sbg.radius.lg, px: 2, py: 1.5 }}>
                <Typography variant="labelMd" component="h3" sx={{ color: sbg.text.primary, mb: 0.5 }}>
                  From the calendar event
                </Typography>
                <DefinitionList items={calendarDetails} sx={{ '& > div:last-of-type': { borderBottom: 0 } }} />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={saving}
          aria-busy={saving || undefined}
          startIcon={saving ? <CircularProgress size={16} thickness={5} color="inherit" aria-hidden="true" /> : undefined}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditEntryDialog;
